import { useState, useCallback, ReactNode } from "react";
import { ReactEditor } from "slate-react";
import { Path, Editor, Transforms } from "slate";

interface ImageDragResult {
  handleDragOver: (e: React.DragEvent) => void;
  handleDragLeave: () => void;
  handleDrop: (e: React.DragEvent) => void;
  DropIndicator: ReactNode;
}

export function useImageDrag(editor: Editor): ImageDragResult {
  const [dropIndicator, setDropIndicator] = useState<{
    top: number;
    left: number;
    width?: number;
    path: Path;
  }>(null);

  const handleDragOver = useCallback(
    (e) => {
      e.preventDefault();

      try {
        const types = Array.from(e.dataTransfer.types);
        if (!types.includes("application/slate-path-fragment")) {
          return;
        }

        e.dataTransfer.dropEffect = "move";
        const editorElement = e.currentTarget; // This is the .slate--editable element
        const editorRect = editorElement.getBoundingClientRect();
        if (editor.children.length === 0 || e.clientY < editorRect.top + 20) {
          setDropIndicator({
            top: 0,
            left: 0,
            width: editorRect.width - 20,
            path: [0],
          });
          return;
        }

        // Find closest element based on mouse position
        try {
          // Get the range at the current position
          const range = ReactEditor.findEventRange(editor, e);
          if (!range) return;
          // Get the DOM point from the event
          const point = { x: e.clientX, y: e.clientY };

          // Find the exact element under the cursor
          const { y } = point;
          let closestElement = null;
          let closestDistance = Infinity;
          let closestRect: DOMRect;

          // Find all block elements and measure distances
          const elements = document.querySelectorAll('[data-slate-node="element"]');
          elements.forEach((el) => {
            // Skip void elements like images
            if (el instanceof HTMLElement && el.dataset.slateVoid === "true") return;

            const rect = el.getBoundingClientRect();
            // Check if point is inside element vertically
            if (y >= rect.top && y <= rect.bottom) {
              // This is a direct hit
              closestElement = el;
              closestRect = rect;
              closestDistance = 0;
              return;
            }

            // If not a direct hit, calculate distance
            const distToTop = Math.abs(y - rect.top);
            const distToBottom = Math.abs(y - rect.bottom);
            const minDist = Math.min(distToTop, distToBottom);

            if (minDist < closestDistance) {
              closestElement = el;
              closestRect = rect;
              closestDistance = minDist;
            }
          });

          // If we found a close element
          if (closestElement && closestRect) {
            const domPath = ReactEditor.findPath(editor, ReactEditor.toSlateNode(editor, closestElement));

            const insertBefore = y < (closestRect.top + closestRect.bottom) / 2;
            const insertPath = insertBefore ? domPath : Path.next(domPath);
            const lineTop = insertBefore ? closestRect.top : closestRect.bottom;

            setDropIndicator({
              top: lineTop - editorRect.top + 66, // 66 is the height of the toolbar + margin of the toolbar
              left: closestRect.left - editorRect.left,
              width: closestRect.width,
              path: insertPath,
            });
          }
        } catch (err) {
          console.error("Error in drag over details:", err);
        }
      } catch (err) {
        console.error("Error in drag over handler:", err);
        setDropIndicator(null);
      }
    },
    [editor]
  );
  const handleDragLeave = useCallback(() => {
    setDropIndicator(null);
  }, []);

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();

      try {
        const data = e.dataTransfer.getData("application/slate-path-fragment");
        if (!data) {
          setDropIndicator(null);
          return;
        }

        const { type, path: sourcePath } = JSON.parse(data);
        if (type !== "image-drag" || !dropIndicator) {
          setDropIndicator(null);
          return;
        }

        const targetPath = dropIndicator.path;

        // Don't move to the same location
        if (
          Path.equals(sourcePath, targetPath) ||
          (Path.hasPrevious(targetPath) && Path.equals(sourcePath, Path.previous(targetPath)))
        ) {
          setDropIndicator(null);
          return;
        }

        // Ensure we're getting the parent element path, not a text node path
        const sourceNodeEntry = Editor.node(editor, sourcePath);
        const sourceElement = sourceNodeEntry[0];

        // Move the node - crucial to do this with withoutNormalizing
        Editor.withoutNormalizing(editor, () => {
          Transforms.removeNodes(editor, { at: sourcePath });

          // Calculate the adjusted target path since the source removal could affect it
          let adjustedPath = targetPath;
          if (Path.isBefore(sourcePath, targetPath)) {
            // If source was before target, target is now one position earlier
            adjustedPath = Path.previous(targetPath);
          }

          // Insert at the target path
          Transforms.insertNodes(editor, sourceElement, { at: adjustedPath });
        });

        setDropIndicator(null);

        // Ensure the editor is focused
        ReactEditor.focus(editor);
      } catch (error) {
        console.error("Error handling drop:", error);
        setDropIndicator(null);
      }
    },
    [editor, dropIndicator]
  );
  const DropIndicator = dropIndicator ? (
    <div
      className="drop-indicator"
      style={{
        top: `${dropIndicator.top}px`,
        left: `${dropIndicator.left}px`,
        width: `${dropIndicator.width}px`,
      }}
    />
  ) : null;

  return {
    DropIndicator,
    handleDragOver,
    handleDragLeave,
    handleDrop,
  };
}
